import type { UploadPort } from "@/shared/contracts/ports";
import { getAuthToken, request } from "@/shared/http/client";

/** Signed object URLs need no panel credentials. Only the API origin may receive them. */
export async function readDocumentBytes(
	uploads: UploadPort,
	id: string,
	signal: AbortSignal,
	maxBytes = 2 * 1024 * 1024,
): Promise<Uint8Array> {
	const file = await uploads.urlFor(id);
	const url = new URL(file.url, window.location.origin);
	const api = new URL(
		import.meta.env.VITE_API_BASE || window.location.origin,
		window.location.origin,
	);
	const local = url.origin === api.origin;
	const token = local ? await getAuthToken() : null;
	const response = await fetch(url.href, {
		signal,
		credentials: local ? "include" : "omit",
		headers: token ? { Authorization: `Bearer ${token}` } : {},
	});
	if (!response.ok || !response.body) throw new Error("preview unavailable");
	const reader = response.body.getReader();
	const chunks: Uint8Array[] = [];
	let bytes = 0;
	try {
		for (;;) {
			const chunk = await reader.read();
			if (chunk.done) break;
			bytes += chunk.value.byteLength;
			if (bytes > maxBytes) throw new Error("preview too large");
			chunks.push(chunk.value);
		}
		const result = new Uint8Array(bytes);
		let offset = 0;
		for (const chunk of chunks) {
			result.set(chunk, offset);
			offset += chunk.byteLength;
		}
		return result;
	} finally {
		await reader.cancel();
	}
}

export interface DocumentVersion {
	id: string;
	path: string;
	name: string;
	mimeType: string;
	sizeBytes: number;
	version: number;
}
export async function documentVersions(
	path: string,
	signal: AbortSignal,
): Promise<DocumentVersion[]> {
	const result = await request<{ versions: DocumentVersion[] }>(
		`/v3/files/history?path=${encodeURIComponent(path)}`,
		{ signal },
	);
	return result.versions;
}

export async function readDocument(
	uploads: UploadPort,
	id: string,
	signal: AbortSignal,
): Promise<string> {
	return new TextDecoder().decode(await readDocumentBytes(uploads, id, signal));
}
export function saveDocument(
	path: string,
	content: string,
	mimeType: string,
	expectedVersion: number,
): Promise<{ file: DocumentVersion }> {
	return request("/v3/files", {
		method: "POST",
		body: { path, content, mimeType, expectedVersion },
	});
}
